/**
 * LaunchAmbassadorsTab - Creator's view of the Ambassadors Program for a launch
 *
 * Before active: explanatory content + configuration
 * After active: allocation table with claim status and X post links
 */

import React, { useState, useEffect } from 'react';
import { FeatherCheck } from '@subframe/core';
import { Button } from '../../ui';
import {
  getLaunchAmbassadors,
  updateLaunchAmbassadorConfig,
  rateAmbassadorXPost,
  type LaunchAmbassadorData,
} from '../../api/ambassadors';

interface LaunchAmbassadorsTabProps {
  launch: {
    id: string;
    status: string;
    total_supply: number | null;
    total_items?: number;
    collection_type?: string;
    ambassador_slots?: number;
    ambassador_selection_completed_at?: string | null;
  };
  onUpdate?: () => void;
}

export const LaunchAmbassadorsTab: React.FC<LaunchAmbassadorsTabProps> = ({ launch, onUpdate }) => {
  const [enabled, setEnabled] = useState((launch.ambassador_slots || 0) > 0);
  const [slots, setSlots] = useState(launch.ambassador_slots || 0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [ambassadorData, setAmbassadorData] = useState<LaunchAmbassadorData | null>(null);
  const [loading, setLoading] = useState(false);

  const [ratingInProgress, setRatingInProgress] = useState<string | null>(null);
  const [pendingRating, setPendingRating] = useState<{ allocId: string; star: number } | null>(null);
  const [hoverRating, setHoverRating] = useState<{ allocId: string; star: number } | null>(null);

  const canConfigure = ['draft', 'pending_approval', 'pending_authorization', 'pending_payment', 'processing', 'ready'].includes(launch.status)
    && !launch.ambassador_selection_completed_at;

  // For build-your-own (composable) collections, use total_items instead of total_supply
  const effectiveSupply = launch.total_supply || launch.total_items || 0;
  const maxSlots = Math.min(Math.floor(effectiveSupply * 0.05), 50);

  useEffect(() => {
    if (launch.ambassador_slots && launch.ambassador_slots > 0) {
      loadAmbassadorData();
    }
  }, [launch.id]);

  async function loadAmbassadorData() {
    setLoading(true);
    try {
      const res = await getLaunchAmbassadors(launch.id);
      if (res?.success) {
        setAmbassadorData(res.data);
      }
    } catch (err) {
      // Non-critical
    } finally {
      setLoading(false);
    }
  }

  async function handleRate(allocId: string, rating: number) {
    setRatingInProgress(allocId);
    try {
      await rateAmbassadorXPost(launch.id, allocId, rating);
      await loadAmbassadorData();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to submit rating');
    } finally {
      setRatingInProgress(null);
    }
  }

  async function handleSave() {
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      const newSlots = enabled ? Math.max(1, Math.min(slots, maxSlots || 50)) : 0;
      await updateLaunchAmbassadorConfig(launch.id, newSlots);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  // Show allocation table for active/completed launches
  if (['active', 'paused', 'completed'].includes(launch.status) && ambassadorData) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-heading-3 font-heading-3 text-default-font">Ambassadors Program</h3>
          <span className="text-body text-subtext-color">
            {ambassadorData.ambassadorSlots} slots configured
          </span>
        </div>

        {/* Summary stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-default-background border border-neutral-border rounded-lg p-4">
            <div className="text-caption text-subtext-color">Selected</div>
            <div className="text-heading-3 font-heading-3 text-default-font">{ambassadorData.summary.totalAllocated}</div>
          </div>
          <div className="bg-default-background border border-neutral-border rounded-lg p-4">
            <div className="text-caption text-subtext-color">Claimed</div>
            <div className="text-heading-3 font-heading-3 text-default-font">
              {ambassadorData.summary.totalClaimed} / {ambassadorData.summary.totalAllocated}
            </div>
          </div>
          <div className="bg-default-background border border-neutral-border rounded-lg p-4">
            <div className="text-caption text-subtext-color">X Posts</div>
            <div className="text-heading-3 font-heading-3 text-default-font">
              {ambassadorData.summary.xPostsSubmitted} / {ambassadorData.summary.totalClaimed}
            </div>
          </div>
          <div className="bg-default-background border border-neutral-border rounded-lg p-4">
            <div className="text-caption text-subtext-color">Rated</div>
            <div className="text-heading-3 font-heading-3 text-default-font">
              {ambassadorData.allocations.filter(a => a.x_post_creator_rating != null).length} / {ambassadorData.summary.xPostsSubmitted}
            </div>
          </div>
          <div className="bg-default-background border border-neutral-border rounded-lg p-4">
            <div className="text-caption text-subtext-color">Missed Deadlines</div>
            <div className="text-heading-3 font-heading-3 text-default-font">{ambassadorData.summary.xPostsMissed}</div>
          </div>
        </div>

        {/* Allocation table */}
        {ambassadorData.allocations.length > 0 ? (
          <div className="border border-neutral-border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-default-background">
                <tr>
                  <th className="px-4 py-3 text-left text-caption font-body-bold text-subtext-color">Ambassador</th>
                  <th className="px-4 py-3 text-left text-caption font-body-bold text-subtext-color">Score</th>
                  <th className="px-4 py-3 text-left text-caption font-body-bold text-subtext-color">Claim Status</th>
                  <th className="px-4 py-3 text-left text-caption font-body-bold text-subtext-color">Claimed At</th>
                  <th className="px-4 py-3 text-left text-caption font-body-bold text-subtext-color">X Post</th>
                  <th className="px-4 py-3 text-left text-caption font-body-bold text-subtext-color">Rating</th>
                </tr>
              </thead>
              <tbody>
                {ambassadorData.allocations.map((alloc) => (
                  <tr key={alloc.id} className="border-t border-neutral-border">
                    <td className="px-4 py-3 text-body text-default-font">
                      {alloc.username || `${alloc.wallet_address.slice(0, 8)}...${alloc.wallet_address.slice(-4)}`}
                    </td>
                    <td className="px-4 py-3 text-body text-default-font">{alloc.score ?? '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-caption font-body-bold ${
                        alloc.status === 'claimed' ? 'bg-success-100 text-success-700' :
                        alloc.status === 'expired' ? 'bg-neutral-100 text-subtext-color' :
                        'bg-warning-100 text-warning-700'
                      }`}>
                        {alloc.status === 'selected' ? 'Pending' : alloc.status.charAt(0).toUpperCase() + alloc.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-body text-subtext-color">
                      {alloc.claimed_at ? new Date(alloc.claimed_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-4 py-3">
                      {alloc.x_post_url ? (
                        <a href={alloc.x_post_url} target="_blank" rel="noopener noreferrer"
                           className="text-brand-600 hover:text-brand-700 text-body">
                          View Post
                        </a>
                      ) : alloc.status === 'claimed' ? (
                        <span className={`text-body ${alloc.x_post_missed ? 'text-error-600' : 'text-warning-600'}`}>
                          {alloc.x_post_missed ? 'Missed deadline' : 'Waiting'}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="px-4 py-3">
                      {alloc.x_post_url ? (
                        alloc.x_post_creator_rating != null ? (
                          <span className="inline-flex gap-0.5">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <span key={star} className={`text-body ${star <= alloc.x_post_creator_rating! ? 'text-warning-500' : 'text-neutral-300'}`}>
                                &#9733;
                              </span>
                            ))}
                          </span>
                        ) : (
                          <div className="inline-flex items-center gap-2">
                            <span className="inline-flex gap-0.5">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <button
                                  key={star}
                                  disabled={ratingInProgress === alloc.id}
                                  onClick={() => setPendingRating({ allocId: alloc.id, star })}
                                  onMouseEnter={() => setHoverRating({ allocId: alloc.id, star })}
                                  onMouseLeave={() => setHoverRating(null)}
                                  className={`text-body cursor-pointer transition-colors disabled:opacity-50 ${
                                    (pendingRating?.allocId === alloc.id && star <= pendingRating.star)
                                      ? 'text-warning-500'
                                      : hoverRating?.allocId === alloc.id && star <= hoverRating.star
                                        ? 'text-warning-400'
                                        : 'text-neutral-300'
                                  }`}
                                >
                                  &#9733;
                                </button>
                              ))}
                            </span>
                            {pendingRating?.allocId === alloc.id && (
                              <span className="inline-flex gap-1">
                                <button
                                  disabled={ratingInProgress === alloc.id}
                                  onClick={() => {
                                    handleRate(alloc.id, pendingRating.star);
                                    setPendingRating(null);
                                  }}
                                  className="px-2 py-0.5 text-caption font-body-bold rounded bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50"
                                >
                                  {ratingInProgress === alloc.id ? '...' : 'Rate'}
                                </button>
                                <button
                                  disabled={ratingInProgress === alloc.id}
                                  onClick={() => setPendingRating(null)}
                                  className="px-2 py-0.5 text-caption rounded border border-neutral-border text-subtext-color hover:bg-default-background disabled:opacity-50"
                                >
                                  Cancel
                                </button>
                              </span>
                            )}
                          </div>
                        )
                      ) : (
                        <span className="text-body text-subtext-color">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-body text-subtext-color">No ambassadors were selected for this launch.</p>
        )}
      </div>
    );
  }

  // Configuration view (before launch is active)
  return (
    <div className="space-y-6">
      {/* Explanatory content */}
      <div>
        <h3 className="text-heading-3 font-heading-3 text-default-font mb-3">Promote Your Launch</h3>
        <p className="text-body text-subtext-color">
          Give your launch a boost by allocating free mints to our Ambassadors. Ambassadors are
          verified, active platform members who will promote your collection on X (Twitter) in exchange
          for a free NFT. They are required to hold the NFT for at least 3 months, so you get genuine
          promotion without the risk of them immediately flipping or flooring your collection.
        </p>
      </div>

      {/* How it works */}
      <div className="border border-neutral-border rounded-lg p-6">
        <h4 className="text-body font-body-bold text-default-font mb-3">How It Works</h4>
        <ol className="list-decimal list-inside space-y-2 text-body text-subtext-color">
          <li>Choose how many ambassador slots you'd like to allocate</li>
          <li>When your launch goes live, the platform automatically selects top-performing ambassadors</li>
          <li>Ambassadors are notified and can claim their free mint during the public mint phase</li>
          <li>Within 24 hours of claiming, ambassadors must post about your project on X</li>
          <li>You can view all ambassador activity, claim status, and X post links right here</li>
        </ol>
      </div>

      {/* Benefits */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {[
          { title: 'Organic Promotion', desc: 'Trusted community members post about your collection on X' },
          { title: '3-Month Hold Period', desc: 'Ambassadors cannot sell, transfer, or list during the lock period' },
          { title: 'Minting Momentum', desc: 'Posts on X drive awareness and encourage more mints' },
          { title: 'Zero Cost', desc: 'No fees beyond the free NFTs you allocate' },
        ].map(({ title, desc }) => (
          <div key={title} className="border border-neutral-border rounded-lg p-4">
            <div className="text-body font-body-bold text-default-font mb-1">{title}</div>
            <div className="text-caption text-subtext-color">{desc}</div>
          </div>
        ))}
      </div>

      {/* Configuration */}
      {canConfigure ? (
        <div className="border border-neutral-border rounded-lg p-6">
          <h4 className="text-body font-body-bold text-default-font mb-4">Configuration</h4>

          <label className="flex items-center gap-3 mb-4 cursor-pointer">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => {
                setEnabled(e.target.checked);
                if (!e.target.checked) setSlots(0);
                else if (slots === 0) setSlots(1);
              }}
              className="w-4 h-4 rounded border-neutral-border"
            />
            <span className="text-body text-default-font">Enable Ambassadors Program for this launch</span>
          </label>

          {enabled && (
            <div className="mb-4">
              <label className="block text-body text-subtext-color mb-1">Number of ambassador slots</label>
              <input
                type="number"
                min={1}
                max={maxSlots || 50}
                value={slots}
                onChange={(e) => setSlots(Math.max(1, Math.min(maxSlots || 50, parseInt(e.target.value) || 1)))}
                className="w-32 px-3 py-2 border border-neutral-border rounded-md text-body text-default-font bg-default-background"
              />
              <p className="text-caption text-subtext-color mt-1">
                Maximum {maxSlots || 50} slots (5% of {effectiveSupply} supply, up to 50)
              </p>
            </div>
          )}

          {error && <p className="text-body text-error-600 mb-4">{error}</p>}

          <div className="flex items-center gap-3">
            <Button onClick={handleSave} disabled={saving} size="medium">
              {saving ? 'Saving...' : 'Save Configuration'}
            </Button>
            {saved && (
              <span className="flex items-center gap-1 text-caption font-caption text-green-600">
                <FeatherCheck className="w-4 h-4" />
                Saved
              </span>
            )}
          </div>
        </div>
      ) : (
        <div className="border border-neutral-border rounded-lg p-6">
          <h4 className="text-body font-body-bold text-default-font mb-2">Configuration</h4>
          <p className="text-body text-subtext-color">
            {launch.ambassador_selection_completed_at
              ? `${launch.ambassador_slots} ambassador slots configured. Ambassadors have been selected and notified.`
              : launch.ambassador_slots && launch.ambassador_slots > 0
                ? `${launch.ambassador_slots} ambassador slots configured. Ambassadors will be selected when the launch is approved and authorized.`
                : 'No ambassador slots configured for this launch.'}
          </p>
          {loading && <p className="text-body text-subtext-color mt-2">Loading ambassador data...</p>}

          {/* Show selected ambassadors after selection */}
          {ambassadorData && ambassadorData.allocations.length > 0 && (
            <div className="mt-4 pt-4 border-t border-neutral-border">
              <h4 className="text-body font-body-bold text-default-font mb-3">
                Selected Ambassadors ({ambassadorData.allocations.length} of {launch.ambassador_slots})
              </h4>
              <div className="space-y-2">
                {ambassadorData.allocations.map((alloc) => (
                  <div key={alloc.id} className="flex items-center justify-between py-2 px-3 rounded-lg bg-default-background border border-neutral-border">
                    <div className="flex items-center gap-3">
                      {alloc.avatar_url && (
                        <img src={alloc.avatar_url} alt="" className="w-6 h-6 rounded-full" />
                      )}
                      <a href={`/wallets/${alloc.wallet_address}`} className="text-body text-brand-600 hover:text-brand-700 hover:underline">
                        {alloc.username || `${alloc.wallet_address.slice(0, 8)}...${alloc.wallet_address.slice(-4)}`}
                      </a>
                    </div>
                    <div className="flex items-center gap-3">
                      {alloc.x_account && (
                        <a
                          href={`https://x.com/${alloc.x_account}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-caption text-subtext-color hover:text-default-font"
                          title={`@${alloc.x_account}`}
                        >
                          @{alloc.x_account}
                        </a>
                      )}
                      <a
                        href={`/inbox?to=${alloc.wallet_address}`}
                        className="text-caption text-brand-600 hover:text-brand-700 hover:underline"
                      >
                        Message
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* FAQ */}
      <div>
        <h3 className="text-heading-3 font-heading-3 text-default-font mb-4">FAQ</h3>
        <div className="space-y-4">
          {[
            { q: 'What are ambassadors?', a: 'Ambassadors are active, vetted platform members with a proven track record of minting and holding NFTs. Each ambassador has a score based on their participation history, hold behavior, and past promotion quality — only members who meet the program\'s requirements can join.' },
            { q: 'Which ambassadors will be selected for my launch?', a: 'Selection happens automatically when your launch goes active. Half the slots go to the highest-scoring ambassadors (guaranteed), and the other half are drawn randomly from the remaining eligible members. This gives top performers priority while still giving newer ambassadors a chance.' },
            { q: 'Will ambassadors floor my collection?', a: 'We can\'t prevent it, but the program is designed to heavily discourage it. Ambassadors who sell, transfer, list, or burn during the 3-month hold period receive severe score penalties (up to 95% reduction), making it very unlikely they\'ll be selected for future launches. Ambassadors with high scores have a lot to lose, so they have strong incentive to hold.' },
            { q: 'How do you verify ambassadors post on X?', a: 'Ambassadors must submit the URL to their X post within 24 hours of claiming their free mint. Creators can view each post directly and rate its quality. Missing the deadline results in a score penalty.' },
            { q: 'What if an ambassador doesn\'t post on X?', a: 'Their program score is penalized, making them less likely to be selected for future launches.' },
            { q: 'Does this reduce my public mint supply?', a: `Yes, ambassador mints come from your total supply during the public mint phase. If you allocate ambassador slots, that many could be claimed for free.` },
            { q: 'What does it cost me?', a: 'Nothing beyond the free NFTs. The platform waives all service fees on ambassador mints.' },
          ].map(({ q, a }) => (
            <div key={q}>
              <p className="text-body font-body-bold text-default-font">{q}</p>
              <p className="text-body text-subtext-color">{a}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
